<template>
  <div v-if="player">
    <card-dialog
      v-model="modalVisible"
      max-width="700px"
      :title="player.id ? 'Edit stream details - ' + player.stream.name : 'Add new stream'"
    >
      <v-card-text
        class="pa-6"
      >
        <validation-observer ref="validationObserver">
          <div class="mb-2 font-weight-bold">
            Stream Name
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="streamForm.name"
              outlined
              :error-messages="errors"
              placeholder="Enter stream name"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Stream Description
          </div>
          <validation-provider
            v-slot="{ errors }"
          >
            <v-textarea
              v-model="streamForm.description"
              outlined
              :error-messages="errors"
              placeholder="Enter description"
            />
          </validation-provider>

          <div v-if="uploadUrl">
            <div class="mb-2 font-weight-bold">
              Stream Cover Image
            </div>
            <file-uploader
              ref="fileUploader"
              v-model="fileUpload"
              custom-drag-handler="#handleDropUpload"
              accept="image/*"
              :upload-url="uploadUrl"
              class="mb-6"
              :additional-form-data="{ }"
            />
          </div>

          <div class="mb-2 font-weight-bold">
            Client
          </div>
          <v-autocomplete
            v-model="streamForm.client_id"
            solo
            flat
            outlined
            persistent-hint
            :items="clients.data"
            :loading="clients.loading"
            clearable
            item-text="name"
            item-value="id"
            placeholder="Select client this stream belongs to"
          />

          <div class="mb-2 font-weight-bold">
            User
          </div>
          <v-text-field
            :value="player.stream.user"
            outlined
            disabled
          />

          <div class="mb-2 font-weight-bold">
            Port
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="streamForm.port"
              outlined
              :error-messages="errors"
              placeholder="Enter port"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Source Secret
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="streamForm.source_secret"
              outlined
              :error-messages="errors"
              placeholder="Enter source secret"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Admin Secret
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="streamForm.admin_secret"
              outlined
              :error-messages="errors"
              placeholder="Enter admin secret"
            />
          </validation-provider>
        </validation-observer>
      </v-card-text>
      <template v-slot:actions>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            color="primary"
            :loading="submitting"
            @click="submit()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import CardDialog from '@/components/helpers/CardDialog'
import Clients from '@/services/Clients'
import Streams from '@/services/Streams'
import FileUploader from '@/components/helpers/FileUploader.vue'

const emptyStreamForm = {
  id: undefined,
  player_id: undefined,
  name: '',
  description: '',
  client_id: undefined,
  port: '',
  source_secret: '',
  admin_secret: ''
}

export default {
  name: 'EditStreamDialog',
  components: { FileUploader, CardDialog },
  props: {
    player: {
      type: Object,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      streamForm: emptyStreamForm,
      submitting: false,
      deleting: false,
      clients: {
        loading: false,
        data: []
      },
      uploadUrl: null,
      fileUpload: []
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  watch: {
    modalVisible(value) {
      if (!value) return

      console.log(value, this.player)

      this.$nextTick(() => {
        if (this.player && this.player.stream) {
          this.streamForm.id = this.player.stream.id
          this.streamForm.player_id = this.player.id
          this.streamForm.name = this.player.stream.name
          this.streamForm.description = this.player.stream.description
          this.streamForm.client_id = this.player.client_id
          this.streamForm.port = this.player.stream.port
          this.streamForm.user = this.player.stream.user
          this.streamForm.admin_secret = this.player.stream.admin_secret
          this.streamForm.source_secret = this.player.stream.source_secret

          this.uploadUrl = process.env.VUE_APP_API_URL + '/streams/' + this.player.stream.id + '/upload-image'
          this.fileUpload = []

          return
        } else {
          this.uploadUrl = null
        }

        this.streamForm = cloneDeep(emptyStreamForm)
      })
    }
  },
  created() {
    this.fetchClients()
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validationObserver.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        let result

        if (this.streamForm.id) {
          if (this.fileUpload.length > 0) {
            this.uploading = true

            await this.$refs.fileUploader.performUpload()

            await this.$nextTick()

            this.uploading = false
          }

          result = await Streams.update(this.streamForm.id, this.streamForm)
        } else {
          result = await Streams.create(this.streamForm)
        }

        this.$emit('stream:updated', result)

        this.$store.dispatch('alert', {
          text: 'Stream successfully ' + (this.streamForm.id ? 'updated' : 'created'),
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting stream',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    }
  }
}
</script>
